import { useConnectorFlowRunner, type UseConnectorFlowRunnerResult } from '@/features/connectors/flows';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useSmsChannelFlow } from './useSmsChannelConnector';
import type { SmsFlowRole } from './constants';

type UseSmsConnectorFlowRunnerOptions = {
  audienceId: string | undefined;
  lang: string;
  /** Prefer this connector id when already known (e.g. SmsWorkspaceProvider). */
  connectorId?: string;
};

export type UseSmsConnectorFlowRunnerResult = UseConnectorFlowRunnerResult & {
  workspaceId: string;
  flow: ReturnType<typeof useSmsChannelFlow>['flow'];
  connectorFlowId: string | undefined;
  connectorInstanceId: string | undefined;
  isChannelLoading: boolean;
};

/**
 * SMS-specific composer: resolve the flow for a role, then run prepare/create.
 * Keeps {@link useConnectorFlowRunner} channel-agnostic.
 */
export const useSmsConnectorFlowRunner = (
  role: SmsFlowRole,
  { audienceId, lang, connectorId }: UseSmsConnectorFlowRunnerOptions
): UseSmsConnectorFlowRunnerResult => {
  const { id: workspaceId } = useWorkspace();
  const { flow, connectorInstanceId, isLoading: isChannelLoading } = useSmsChannelFlow(role, { connectorId });
  const connectorFlowId = flow?.id;

  const runner = useConnectorFlowRunner({
    workspaceId,
    audienceId,
    connectorFlowId,
    connectorInstanceId,
    lang,
  });

  return {
    ...runner,
    workspaceId,
    flow,
    connectorFlowId,
    connectorInstanceId,
    isChannelLoading,
  };
};
